import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from "./icons";
import { cn, focusRing } from "./utils";

type MenuItemBase = {
  id: string;
  label: string;
  icon?: ReactNode;
  tone?: "default" | "danger";
  /**
   * The keys that run it, such as "Mod+Shift+U". Shown on the right of the item. Mod is ⌘ on
   * Apple devices and Ctrl elsewhere.
   */
  shortcut?: string;
  /** Shows a tick when true, for the current choice, such as Mentions only. */
  checked?: boolean;
  /** Why it cannot run now. The item stays in the menu, greyed, with this as its explanation. */
  disabled?: string;
};

export type MenuItem =
  | (MenuItemBase & { type?: "item"; onSelect: () => void })
  /** Opens more choices, such as how long to mute for. */
  | (MenuItemBase & { type: "submenu"; items: MenuItem[] })
  | { type: "separator"; id: string };

export type Actionable = Exclude<MenuItem, { type: "separator" }>;

export type MenuProps = {
  label: string;
  items: MenuItem[];
  onClose?: () => void;
  autoFocus?: boolean;
  /** A popover beside the pointer, or the rows of a phone sheet, where submenus open as a second page. */
  variant?: "popover" | "sheet";
  className?: string;
  style?: CSSProperties;
};

const TYPEAHEAD_MS = 700;
/** How long the pointer rests on an item before its submenu opens, so passing over it does nothing. */
const SUBMENU_OPEN_MS = 120;
/** How long a submenu stays while the pointer crosses other items on its way to it. */
const SUBMENU_CLOSE_MS = 300;
const EDGE = 8;

/** Whether this is an Apple device, where shortcuts use ⌘ in place of Ctrl. */
export function isApple(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return /Mac|iPhone|iPad|iPod/i.test(nav.userAgentData?.platform || nav.platform || nav.userAgent || "");
}

const APPLE_KEYS: Record<string, string> = { mod: "⌘", shift: "⇧", alt: "⌥", ctrl: "⌃", enter: "↩" };
const OTHER_KEYS: Record<string, string> = {
  mod: "Ctrl",
  shift: "Shift",
  alt: "Alt",
  ctrl: "Ctrl",
  enter: "Enter",
};
const ARIA_KEYS: Record<string, string> = { shift: "Shift", alt: "Alt", ctrl: "Control", enter: "Enter" };

/** How a shortcut such as "Mod+Shift+U" reads on this device: ⌘⇧U on a Mac, Ctrl+Shift+U elsewhere. */
export function formatShortcut(shortcut: string, apple = isApple()): string {
  const parts = shortcut.split("+").map((p) => {
    const key = p.trim().toLowerCase();
    const names = apple ? APPLE_KEYS : OTHER_KEYS;
    return names[key] ?? (p.length === 1 ? p.toUpperCase() : p);
  });
  return parts.join(apple ? "" : "+");
}

/** The same shortcut in the form screen readers expect, such as "Meta+Shift+U". */
function ariaShortcut(shortcut: string, apple: boolean): string {
  return shortcut
    .split("+")
    .map((p) => {
      const key = p.trim().toLowerCase();
      if (key === "mod") return apple ? "Meta" : "Control";
      return ARIA_KEYS[key] ?? (p.length === 1 ? p.toUpperCase() : p);
    })
    .join("+");
}

/** The parts of a key press a shortcut is matched against. */
export type KeyPress = Pick<
  globalThis.KeyboardEvent,
  "key" | "code" | "altKey" | "metaKey" | "ctrlKey" | "shiftKey"
>;

/**
 * Whether a key press is the shortcut, such as "Alt+U", "Mod+I", "R" or "Delete". Letters are
 * matched by the key's place as well as its character, because Option changes the character on
 * a Mac: Option and U types a diaeresis.
 */
export function matchesShortcut(e: KeyPress, shortcut: string, apple = isApple()): boolean {
  const parts = shortcut.split("+").map((p) => p.trim().toLowerCase());
  const key = parts[parts.length - 1] ?? "";
  const want = new Set(parts.slice(0, -1));
  const mod = want.has("mod");
  const meta = (mod && apple) || want.has("meta");
  const ctrl = (mod && !apple) || want.has("ctrl");
  if (e.metaKey !== meta || e.ctrlKey !== ctrl) return false;
  if (e.altKey !== want.has("alt") || e.shiftKey !== want.has("shift")) return false;
  const pressed = e.key.toLowerCase();
  if (key.length === 1 && /[a-z]/.test(key)) return pressed === key || e.code === `Key${key.toUpperCase()}`;
  if (key === "delete") return pressed === "delete" || pressed === "backspace";
  return pressed === key;
}

/** The item a key press runs, searching submenus too. Disabled items never match. */
export function itemForShortcut(items: MenuItem[], e: KeyPress, apple = isApple()): Actionable | null {
  for (const item of items) {
    if (item.type === "separator" || item.disabled) continue;
    if (item.type === "submenu") {
      const inner = itemForShortcut(item.items, e, apple);
      if (inner) return inner;
      continue;
    }
    if (item.shortcut && matchesShortcut(e, item.shortcut, apple)) return item;
  }
  return null;
}

/** Whether keys pressed here are someone typing, which shortcuts must leave alone. */
export function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return Boolean(el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
}

function isPrintable(e: KeyboardEvent) {
  return e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== " ";
}

/** A submenu beside the item that opened it, flipped to the left when there is no room on the right. */
function Flyout({
  trigger,
  children,
  onPointerEnter,
}: {
  trigger: HTMLElement;
  children: ReactNode;
  onPointerEnter: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number; side: "right" | "left" } | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const at = trigger.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    let left = at.right - 2;
    let side: "right" | "left" = "right";
    if (left + width > window.innerWidth - EDGE) {
      left = at.left - width + 2;
      side = "left";
    }
    left = Math.max(EDGE, left);
    const top = Math.max(EDGE, Math.min(at.top - 6, window.innerHeight - height - EDGE));
    setPlace({ left, top, side });
  }, [trigger]);

  useLayoutEffect(measure, [measure]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <div
      ref={ref}
      data-flyout=""
      data-side={place?.side}
      onPointerEnter={onPointerEnter}
      // Hidden by opacity while it is measured, so its first item can still take focus.
      className={cn("fixed z-10", !place && "pointer-events-none opacity-0")}
      style={place ? { left: place.left, top: place.top } : { left: 0, top: 0 }}
    >
      {children}
    </div>
  );
}

// Every list and message menu. Arrow keys, Home, End and typing a letter move between items.
// Escape or Tab closes it. Submenus open on hover, with the right arrow, or with Enter.
export function Menu({
  label,
  items,
  onClose,
  autoFocus = true,
  variant = "popover",
  className,
  style,
  onBack,
  onTab,
}: MenuProps & {
  /** For a submenu: goes back to the menu that opened it. */
  onBack?: () => void;
  /** Tab moves on to something beside the menu, in place of closing it. */
  onTab?: (backwards: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Marks this menu's own items, so a submenu's items inside it are not counted as its own.
  const scope = useId().replace(/[^\w-]/g, "");
  const sheet = variant === "sheet";
  const apple = isApple();
  // On a phone, a submenu replaces the list with a page of its own and a way back.
  const [page, setPage] = useState<{ label: string; items: MenuItem[]; from: string } | null>(null);
  // On desktop, a submenu opens beside its item. `focus` is set when the keyboard opened it.
  const [sub, setSub] = useState<{ id: string; focus: boolean } | null>(null);
  const triggers = useRef(new Map<string, HTMLElement>());
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const typed = useRef({ text: "", at: 0 });

  const shown = page ? page.items : items;

  const nodes = () => Array.from(ref.current?.querySelectorAll<HTMLElement>(`[data-menu="${scope}"]`) ?? []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: focus the first item when a page opens
  useEffect(() => {
    if (autoFocus || page) nodes()[0]?.focus({ preventScroll: false });
  }, [autoFocus, page]);

  useEffect(
    () => () => {
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
    },
    [],
  );

  const goBack = (from: string | undefined) => {
    setPage(null);
    // Focus goes back to the item that opened the page.
    window.requestAnimationFrame(() =>
      nodes()
        .find((n) => n.dataset.item === from)
        ?.focus(),
    );
  };

  const closeSub = (refocus: boolean) => {
    const id = sub?.id;
    setSub(null);
    if (refocus && id) triggers.current.get(id)?.focus();
  };

  const activate = (item: Actionable, keyboard: boolean) => {
    if (item.disabled) return;
    if (item.type === "submenu") {
      if (sheet) setPage({ label: item.label, items: item.items, from: item.id });
      else setSub({ id: item.id, focus: keyboard });
      return;
    }
    item.onSelect();
  };

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const list = nodes();
    const index = list.indexOf(document.activeElement as HTMLElement);
    const handled = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    const move = (to: number) => {
      handled();
      list[(to + list.length) % list.length]?.focus();
    };
    const current = index >= 0 ? (list[index]?.dataset.item ?? "") : "";
    const item = shown.find((i): i is Actionable => i.type !== "separator" && i.id === current);

    if (e.key === "ArrowDown") move(index + 1);
    else if (e.key === "ArrowUp") move(index < 0 ? list.length - 1 : index - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(list.length - 1);
    else if (e.key === "ArrowRight" && item?.type === "submenu") {
      handled();
      activate(item, true);
    } else if (e.key === "ArrowLeft" && (onBack || page)) {
      handled();
      if (page) goBack(page.from);
      else onBack?.();
    } else if (e.key === "Escape") {
      handled();
      if (page) goBack(page.from);
      else if (onBack) onBack();
      else onClose?.();
    } else if (e.key === "Tab") {
      // Tab leaves the menu, which closes it. Focus goes back to what opened it.
      e.preventDefault();
      e.stopPropagation();
      if (onTab) onTab(e.shiftKey);
      else onClose?.();
    } else if (isPrintable(e)) {
      handled();
      const now = Date.now();
      const key = e.key.toLowerCase();
      const fresh = now - typed.current.at > TYPEAHEAD_MS;
      const text = fresh ? key : typed.current.text + key;
      typed.current = { text, at: now };
      // Typing the same letter again moves on to the next item that starts with it.
      const same = text.split("").every((c) => c === key);
      const labels = list.map((n) => (n.dataset.label ?? "").toLowerCase());
      const find = (prefix: string, from: number) => {
        for (let step = 0; step < list.length; step++) {
          const i = (from + step) % list.length;
          if (labels[i]?.startsWith(prefix)) return i;
        }
        return -1;
      };
      let found = find(same ? key : text, fresh || same ? index + 1 : Math.max(index, 0));
      // Letters that match nothing together start again from the last one typed.
      if (found < 0 && !same) {
        typed.current = { text: key, at: now };
        found = find(key, index + 1);
      }
      if (found >= 0) list[found]?.focus();
    }
  }

  const row = sheet
    ? "min-h-[52px] gap-3.5 rounded-field px-3 py-2.5 text-base"
    : "min-h-10 gap-3 rounded-[10px] px-3 py-2 text-[14.5px]";

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={page ? page.label : label}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      style={style}
      className={cn(
        "flex flex-col overflow-y-auto overscroll-contain bg-linen p-1.5 outline-none",
        sheet
          ? "w-full"
          : "w-max min-w-[232px] max-w-[min(340px,calc(100vw-16px))] rounded-[16px] shadow-menu",
        className,
      )}
    >
      {page && (
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          data-menu={scope}
          data-item="__back"
          data-label="Back"
          onClick={() => goBack(page.from)}
          className={cn(
            "flex flex-none cursor-pointer items-center text-left font-semibold text-ink",
            row,
            "hover:bg-surface-header focus-visible:bg-surface-header",
            focusRing,
          )}
        >
          <ChevronLeftIcon size={20} strokeWidth={2.2} />
          <span className="min-w-0 flex-1">{page.label}</span>
          <span className="sr-only">, back</span>
        </button>
      )}
      {shown.map((item) => {
        if (item.type === "separator")
          return <hr key={item.id} className="mx-2 my-1 h-px flex-none border-0 bg-line" />;
        const isSub = item.type === "submenu";
        const open = sub?.id === item.id;
        const role = item.checked !== undefined ? "menuitemcheckbox" : "menuitem";
        return (
          <div key={item.id} className="flex flex-none flex-col">
            <button
              ref={(el) => {
                if (el) triggers.current.set(item.id, el);
                else triggers.current.delete(item.id);
              }}
              type="button"
              role={role}
              tabIndex={-1}
              data-menu={scope}
              data-item={item.id}
              data-label={item.label}
              {...(item.checked !== undefined ? { "aria-checked": item.checked } : {})}
              aria-disabled={item.disabled ? true : undefined}
              aria-haspopup={isSub ? "menu" : undefined}
              aria-expanded={isSub && !sheet ? open : undefined}
              aria-keyshortcuts={item.shortcut ? ariaShortcut(item.shortcut, apple) : undefined}
              title={item.disabled || undefined}
              onClick={(e) => activate(item, e.detail === 0)}
              onPointerMove={(e) => {
                if (e.pointerType !== "mouse") return;
                if (document.activeElement !== e.currentTarget)
                  e.currentTarget.focus({ preventScroll: true });
                if (sheet) return;
                window.clearTimeout(openTimer.current);
                if (isSub && !open && !item.disabled) {
                  window.clearTimeout(closeTimer.current);
                  openTimer.current = window.setTimeout(
                    () => setSub({ id: item.id, focus: false }),
                    SUBMENU_OPEN_MS,
                  );
                } else if (sub && !open) {
                  window.clearTimeout(closeTimer.current);
                  closeTimer.current = window.setTimeout(() => setSub(null), SUBMENU_CLOSE_MS);
                }
              }}
              className={cn(
                "flex w-full cursor-pointer items-center text-left",
                row,
                item.disabled
                  ? "cursor-default text-ink-muted"
                  : item.tone === "danger"
                    ? "font-semibold text-scarlet"
                    : "text-ink",
                "hover:bg-surface-header focus-visible:bg-surface-header",
                open && "bg-surface-header",
                focusRing,
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{item.label}</span>
                {sheet && item.disabled && (
                  <span className="text-[13px] font-normal text-ink-meta">{item.disabled}</span>
                )}
              </span>
              {item.shortcut && !sheet && (
                <span aria-hidden="true" className="flex-none pl-4 text-[13px] text-ink-meta">
                  {formatShortcut(item.shortcut, apple)}
                </span>
              )}
              {item.checked ? (
                <CheckIcon size={18} strokeWidth={2.4} className="flex-none text-blue" />
              ) : (
                item.icon
              )}
              {isSub && <ChevronRightIcon size={17} strokeWidth={2.2} className="flex-none text-ink-meta" />}
            </button>
            {isSub && open && !sheet && triggers.current.get(item.id) && (
              <Flyout
                trigger={triggers.current.get(item.id) as HTMLElement}
                onPointerEnter={() => window.clearTimeout(closeTimer.current)}
              >
                <Menu
                  label={item.label}
                  items={item.items}
                  autoFocus={sub.focus}
                  onBack={() => closeSub(true)}
                  onClose={onClose}
                  style={{ maxHeight: "calc(100dvh - 16px)" }}
                />
              </Flyout>
            )}
          </div>
        );
      })}
    </div>
  );
}

export type ReactionOption = { emoji: string; label: string };

export const DEFAULT_REACTIONS: ReactionOption[] = [
  { emoji: "👍", label: "thumbs up" },
  { emoji: "❤️", label: "heart" },
  { emoji: "😂", label: "laughing" },
  { emoji: "😮", label: "surprise" },
  { emoji: "🙏", label: "thanks" },
  { emoji: "✅", label: "done" },
];

export function ReactionBar({
  options = DEFAULT_REACTIONS,
  selected,
  onReact,
  onMore,
  className,
}: {
  options?: ReactionOption[];
  selected?: string;
  onReact: (emoji: string) => void;
  onMore?: () => void;
  className?: string;
}) {
  return (
    <fieldset
      aria-label="Reactions"
      className={cn("m-0 flex items-center gap-0.5 rounded-full border-0 bg-linen p-1.5", className)}
    >
      {options.map((o) => (
        <button
          key={o.emoji}
          type="button"
          aria-label={`React with ${o.label}`}
          aria-pressed={selected === o.emoji}
          onClick={() => onReact(o.emoji)}
          className={cn(
            "h-[46px] w-[46px] cursor-pointer rounded-full text-[27px] leading-none",
            selected === o.emoji ? "bg-blue-tint" : "bg-transparent hover:bg-surface-header",
            focusRing,
          )}
        >
          {o.emoji}
        </button>
      ))}
      {onMore && (
        <button
          type="button"
          aria-label="More reactions"
          onClick={onMore}
          className={cn(
            "flex h-[46px] w-[46px] cursor-pointer items-center justify-center rounded-full bg-surface-header text-ink",
            focusRing,
          )}
        >
          <PlusIcon size={19} strokeWidth={2.4} />
        </button>
      )}
    </fieldset>
  );
}
